/**
 * Modal para editar pipeline
 * Permite controlar acesso em dois níveis:
 * 1. Nível de Representação/Tenant (quem compartilha)
 * 2. Nível de Perfil de Usuário (quem vê dentro do tenant)
 * Segue o padrão do modal de metas
 */

import { useState, useEffect, useMemo } from 'react';
import { Modal, Button, message, Checkbox, Select, Spin } from 'antd';
import { X } from 'lucide-react';
import { Pipeline, PipelinePerfilUsuario } from '../types';
import { useUpdatePipeline } from '../hooks/useUpdatePipeline';
import { useTenants } from '../hooks/useTenants';

// NÍVEL 2: Perfis de usuário dentro do tenant
const PERFIS_OPTIONS: Array<{
  value: PipelinePerfilUsuario;
  label: string;
  description: string;
}> = [
  {
    value: 'relacionador',
    label: 'Relacionador',
    description: 'Qualificação de leads'
  },
  {
    value: 'lider_vendas',
    label: 'Líder de Vendas',
    description: 'Gerencia equipe de vendas'
  },
  {
    value: 'supervisor',
    label: 'Supervisor',
    description: 'Supervisiona operações'
  },
  {
    value: 'especialista_pos_venda',
    label: 'Especialista Pós-venda',
    description: 'Acompanhamento de clientes'
  },
  {
    value: 'admin',
    label: 'Administrador',
    description: 'Acesso total'
  }
];

interface PipelineEditModalProps {
  pipeline: Pipeline | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function PipelineEditModal({
  pipeline,
  open,
  onClose,
  onSuccess
}: PipelineEditModalProps) {
  const [nome, setNome] = useState('');
  const [selectedRepresentacoes, setSelectedRepresentacoes] = useState<number[]>([]);
  const [permitirAssociados, setPermitirAssociados] = useState(false);
  const [selectedPerfis, setSelectedPerfis] = useState<PipelinePerfilUsuario[]>([]);
  const updatePipeline = useUpdatePipeline();
  const { data: tenants = [], isLoading: tenantesCarregando } = useTenants();

  // Formata tenants para o select
  const tenantOptions = useMemo(
    () => tenants.map((tenant) => ({ value: tenant.id, label: tenant.nome })),
    [tenants]
  );

  useEffect(() => {
    if (open && pipeline) {
      setNome(pipeline.nome);
      // Se perfil_usuario é um array, usa direto, senão coloca em um array
      const perfis = Array.isArray(pipeline.perfil_usuario)
        ? pipeline.perfil_usuario
        : pipeline.perfil_usuario
        ? [pipeline.perfil_usuario]
        : [];
      setSelectedPerfis(perfis);

      // Carregar representações e permissão de associados do banco
      const tenantIds = Array.isArray(pipeline.tenant_ids)
        ? pipeline.tenant_ids
        : pipeline.tenant_ids
        ? [pipeline.tenant_ids]
        : [];
      setSelectedRepresentacoes(tenantIds);
      setPermitirAssociados(pipeline.permitir_associados ?? false);
    }
  }, [open, pipeline]);

  const handleSave = async () => {
    if (!pipeline || !nome.trim()) {
      message.error('Nome da pipeline é obrigatório');
      return;
    }

    if (selectedRepresentacoes.length === 0) {
      message.error('Selecione pelo menos uma representação');
      return;
    }

    if (selectedPerfis.length === 0) {
      message.error('Selecione pelo menos um perfil');
      return;
    }

    try {
      // Se há apenas um perfil, salva como valor único. Se há vários, salva como array
      const perfilUsuario = selectedPerfis.length === 1 ? selectedPerfis[0] : selectedPerfis;

      await updatePipeline.mutateAsync({
        id: pipeline.id,
        nome: nome.trim(),
        perfil_usuario: perfilUsuario,
        tenant_ids: selectedRepresentacoes,
        permitir_associados: permitirAssociados
      });

      message.success('Pipeline atualizada com sucesso');
      onClose();
      onSuccess?.();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Erro ao atualizar');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={520}
      destroyOnHidden
      centered
      className="pipeline-edit-modal"
      styles={{
        mask: { background: 'rgba(0, 0, 0, 0.45)' },
        body: { padding: 0, overflow: 'hidden' },
      }}
      zIndex={1000}
    >
      {/* Botão fechar */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-gray-400 transition"
        aria-label="Fechar"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex flex-col h-auto bg-white dark:bg-zinc-900">
        {/* Header */}
        <div className="bg-[#0d0d0f] dark:bg-zinc-800 text-white px-6 py-4 flex-shrink-0">
          <p className="text-[11px] text-white/50 uppercase tracking-wide mb-1">
            Editar Pipeline
          </p>
          <h2 className="text-[15px] font-semibold">
            Atualize os dados da pipeline
          </h2>
        </div>

        {/* Conteúdo */}
        <div className="px-6 py-6 flex-1 overflow-y-auto">
          <div className="space-y-6">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Nome da Pipeline
              </label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Qualificação"
                disabled={pipeline?.fixa}
                className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FF3737] focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
              />
              {pipeline?.fixa && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Nome de pipelines fixas não pode ser alterado.
                </p>
              )}
            </div>

            {/* NÍVEL 1: Representações específicas + Associados */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                Nível 1: Quais representações podem compartilhar?
              </label>
              <Spin spinning={tenantesCarregando}>
                <Select
                  mode="multiple"
                  value={selectedRepresentacoes}
                  onChange={setSelectedRepresentacoes}
                  options={tenantOptions}
                  placeholder={tenantesCarregando ? 'Carregando representações...' : 'Selecione representações...'}
                  style={{ width: '100%' }}
                  disabled={tenantesCarregando}
                />
              </Spin>

              {/* Checkbox: Permitir Associados */}
              <div className="mt-3 flex items-start">
                <Checkbox
                  checked={permitirAssociados}
                  onChange={(e) => setPermitirAssociados(e.target.checked)}
                  className="mt-0.5"
                />
                <div className="ml-2 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Permitir que Associados compartilhem
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Se habilitado, associados destas representações também podem gerenciar esta pipeline
                  </div>
                </div>
              </div>
            </div>

            {/* NÍVEL 2: Perfis de Usuário */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                Nível 2: Quais perfis podem acessar dentro da representação?
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-zinc-700 rounded-lg p-3 bg-gray-50/50 dark:bg-zinc-800/50 space-y-2">
                {PERFIS_OPTIONS.map((perfil) => (
                  <label
                    key={perfil.value}
                    className="flex items-start p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-700 cursor-pointer transition"
                  >
                    <Checkbox
                      checked={selectedPerfis.includes(perfil.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPerfis([...selectedPerfis, perfil.value]);
                        } else {
                          setSelectedPerfis(
                            selectedPerfis.filter((p) => p !== perfil.value)
                          );
                        }
                      }}
                      className="mt-0.5"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {perfil.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {perfil.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-2">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <span className="font-medium">📍 Nível 1 (Representações):</span> Selecione quais representações podem compartilhar esta pipeline. O checkbox permite que Associados também gerenciem.
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <span className="font-medium">👤 Nível 2 (Perfis):</span> Define quais perfis dentro daquelas representações podem visualizar/usar a pipeline.
              </p>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="flex gap-3 mt-8 pt-4 border-t border-gray-200 dark:border-zinc-700">
            <Button onClick={onClose} style={{ flex: 1 }}>
              Cancelar
            </Button>
            <Button
              type="primary"
              loading={updatePipeline.isPending}
              onClick={handleSave}
              style={{ flex: 1, backgroundColor: '#FF3737' }}
            >
              {updatePipeline.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
